<template>
	<LayoutBreadcrumbs class="main-wrapper" :with-schema="false" />

	<div
		v-if="selectedProduct && Object.keys(selectedProduct).length"
		class="flex flex-col w-full lg:!pl-0 main-wrapper"
	>
		<div class="flex lg:gap-0 gap-2 flex-col lg:flex-row w-full">
			<div
				v-if="selectedProduct?.Files"
				class="lg:max-w-[50%] lg:w-full xl:min-w-[664px] -mx-4 lg:mx-0"
			>
				<StoryblokComponent
					v-for="blok in imageGallery"
					:key="blok._uid"
					:blok="blok"
					:images="
						selectedProduct.Files.length > 0
							? selectedProduct.Files
							: [defaultImage]
					"
					:price-list-id="
						specialPrice?.enabled ? selectedProduct.PriceListId : null
					"
					:special-price="specialPrice"
				/>
			</div>

			<div class="w-full lg:w-1/2 lg:px-[50px] xl:min-w-[488px] lg:flex">
				<div
					class="lg:sticky lg:bottom-0 lg:self-end lg:pt-[70px] lg:pb-5 lg:w-full lg:min-h-[calc(100vh-100px)]"
				>
					<StoryblokComponent
						v-for="blok in productAttributes"
						:key="blok._uid"
						:blok="blok"
						:parametrics="parametrics"
						:main-parametrics="mainParametrics"
					/>
					<StoryblokComponent
						v-for="blok in productDetails"
						:key="blok._uid"
						:blok="blok"
						:size-guide-tab="sizeGuideTab"
						:washing-instructions-tab="washingInstructionsTab"
					/>
				</div>
			</div>
		</div>
		<LazyLoadWrapper>
			<div class="lg:pl-4 pt-8 grid grid-cols-1 gap-8">
				<ProductSimilarProducts v-if="selectedProduct" />
				<ProductPackageProducts
					v-if="selectedProduct"
					:product="selectedProduct"
				/>
				<StoryblokComponent
					v-for="blok in productRecommendations"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
		</LazyLoadWrapper>
	</div>
</template>

<script setup lang="ts">
import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import type {
	BlokGenericForComponents,
	BlokSizeGuideTab,
	BlokWashingInstructionsTab,
	SpecialPrice
} from "~/types/storyblok/storyblok";

interface Props {
	parametrics: GroupedParametrics | undefined;
	imageGallery: BlokGenericForComponents[] | undefined;
	productAttributes: BlokGenericForComponents[] | undefined;
	productDetails: BlokGenericForComponents[] | undefined;
	colorFromParametricsInLink: ParametricListValue | undefined;
	specialPrice?: SpecialPrice | null;
	productRecommendations: BlokGenericForComponents[] | undefined;
	sizeGuideTab?: BlokSizeGuideTab;
	washingInstructionsTab?: BlokWashingInstructionsTab;
}

const {
	parametrics,
	imageGallery,
	productAttributes,
	productDetails,
	colorFromParametricsInLink,
	sizeGuideTab,
	washingInstructionsTab
} = defineProps<Props>();

const {
	getAllSecondParametrics,
	selectProductVariant,
	getSecondParametricName,
	helloRetailApi
} = useNorcePwa();
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();
const { productDetail } = useGtmEvents();
const { productBrowse } = useDotdigital();

const { selectedProduct, products, selectedColor, selectedSecondParametric } =
	storeToRefs(useProductStore());

const productStore = useProductStore();
const defaultImage = computed(() => {
	return {
		id: "default-id",
		Key: "default-key",
		Name: selectedProduct?.value?.Name,
		Path: productStore.defaultProductImage // This will always have the latest value
	};
});
const mainParametrics = computed(() => {
	return productAttributes?.[0]?.main_parametrics || null;
});

watch(selectedSecondParametric, () => {
	if (parametrics) {
		useProductStore().addSelectedProduct(
			selectProductVariant({
				selectedColorId: selectedColor.value?.Id,
				selectedSecondParametricId: selectedSecondParametric.value?.Id,
				products: products.value!,
				parametricsAvailable:
					parametrics?.color ??
					parametrics?.[getSecondParametricName(parametrics)]
			})
		);
	}
});

watch(selectedColor, () => {
	if (
		parametrics &&
		parametrics[getSecondParametricName(parametrics)] &&
		!colorFromParametricsInLink &&
		selectedColor.value
	) {
		const parametricsSecondOneLocal = getAllSecondParametrics({
			parametrics: parametrics[getSecondParametricName(parametrics)],
			products: products.value!,
			selectedColor: selectedColor.value
		});
		const selectedSizeId = selectedProduct.value?.VariantParametrics?.find(
			(parametric) => parametric.Code === getSecondParametricName(parametrics)
		)?.ValueId;
		const selectedSecondParametricLocal = parametricsSecondOneLocal.find(
			(parametric) => parametric.Id === selectedSizeId
		);

		if (selectedSecondParametricLocal) {
			useProductStore().addSelectedSecondParametric(
				selectedSecondParametricLocal
			);
		}
	}
});

watch(selectedProduct, () => {
	if (selectedProduct.value) {
		productDetail(selectedProduct.value);
		productBrowse(selectedProduct.value);
	}
});

onMounted(() => {
	if (selectedProduct.value) {
		productDetail(selectedProduct.value);
		productBrowse(selectedProduct.value);
	}

	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			productNumber: selectedProduct.value?.PartNo || "",
			referrer: window.location.origin
		}
	});
});
</script>
